import React, { useEffect, useState } from 'react';
import { TrendingUpIcon, AlertTriangleIcon, CheckCircleIcon, InfoIcon, ZapIcon, PauseIcon, XIcon, ChevronRightIcon } from 'lucide-react';
interface PipelineGroup {
  id: string;
  name: string;
  rating: string;
  invoices: number;
  totalValue: number;
  trades: number;
  collection: number;
  avgDays: number;
  deals: Array<{
    id: string;
    supplier: string;
    amount: number;
    model: string;
    category: string;
  }>;
}
interface AllocationItem {
  id: string;
  obligor: string;
  supplier: string;
  amount: number;
  status: 'VERIFYING' | 'UCC FILING' | 'SETTLED';
  sla: string;
  priority?: 'HIGH';
}
/** seed queue used on first render */
const INITIAL_ALLOCATION_QUEUE: AllocationItem[] = [{
  id: 'INV-10231',
  obligor: 'Walmart',
  supplier: 'Premium Foods',
  amount: 426000,
  status: 'VERIFYING',
  sla: '28m'
}, {
  id: 'INV-10232',
  obligor: 'Walmart',
  supplier: 'TechGear',
  amount: 793000,
  status: 'UCC FILING',
  sla: '14m',
  priority: 'HIGH'
}, {
  id: 'INV-10234',
  obligor: 'Target',
  supplier: 'Global Logistics',
  amount: 805000,
  status: 'SETTLED',
  sla: 'SETTLED'
}];
export function FundCapitalization() {
  const [activeStrategy, setActiveStrategy] = useState<'CONSERVATIVE' | 'BALANCED' | 'AGGRESSIVE'>('BALANCED');
  const [expandedPipeline, setExpandedPipeline] = useState<string | null>(null);
  const [allocationQueue, setAllocationQueue] = useState<AllocationItem[]>(INITIAL_ALLOCATION_QUEUE);
  useEffect(() => {
    document.title = 'Mint | Allocation Engine';
  }, []);
  const pipelineGroups: PipelineGroup[] = [{
    id: 'walmart',
    name: 'Walmart Suppliers',
    rating: 'A+',
    invoices: 224,
    totalValue: 18.2,
    trades: 1247,
    collection: 99.2,
    avgDays: 28,
    deals: [{
      id: 'deal-1',
      supplier: 'Premium Foods LLC',
      amount: 3.2,
      model: '+12.4%',
      category: 'Food'
    }, {
      id: 'deal-2',
      supplier: 'TechGear Solutions',
      amount: 5.1,
      model: '+11.8%',
      category: 'Electronics'
    }, {
      id: 'deal-3',
      supplier: 'Fulfillco Logistics',
      amount: 2.4,
      model: '+13.1%',
      category: 'Logistics'
    }]
  }, {
    id: 'target',
    name: 'Target Corp Vendors',
    rating: 'A',
    invoices: 87,
    totalValue: 9.4,
    trades: 536,
    collection: 99,
    avgDays: 31,
    deals: []
  }, {
    id: 'amazon',
    name: 'Amazon Vendors',
    rating: 'AA-',
    invoices: 142,
    totalValue: 11.1,
    trades: 889,
    collection: 99.1,
    avgDays: 24,
    deals: []
  }];
  const agingBuckets = [{
    range: '0-30',
    pct: 73.4,
    value: 72.3
  }, {
    range: '31-60',
    pct: 19.5,
    value: 19.2
  }, {
    range: '61-90',
    pct: 7.0,
    value: 6.9
  }, {
    range: '>90',
    pct: 0.1,
    value: 0.1
  }];
  /** convert group + deal into a queue row and append to Allocation Queue */
  const handleAddDealToQueue = (group: PipelineGroup, deal: PipelineGroup['deals'][number]) => {
    // derive obligor from group name (e.g. "Walmart Suppliers" -> "Walmart")
    const obligor = group.name.replace(' Suppliers', '').replace(' Vendors', '').replace(' Corp', '') || group.name;
    // amount in queue is stored as whole dollars; deals are in millions
    const amount = Math.round(deal.amount * 1000000);
    // simple id generator for demo; real system would use invoice id
    const newId = `INV-${Math.floor(10000 + Math.random() * 90000)}`;
    const newItem: AllocationItem = {
      id: newId,
      obligor,
      supplier: deal.supplier,
      amount,
      status: 'VERIFYING',
      sla: '30m'
    };
    setAllocationQueue(prev => {
      // avoid duplicates by supplier + obligor + amount
      const exists = prev.some(row => row.obligor === newItem.obligor && row.supplier === newItem.supplier && row.amount === newItem.amount);
      if (exists) return prev;
      return [...prev, newItem];
    });
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-1186">
      {/* Header */}
      <div className="mb-6" data-id="element-1187">
        <div className="flex items-center justify-between" data-id="element-1188">
          <h1 className="text-3xl font-semibold text-gray-900" data-id="element-1189">
            Allocation Engine
          </h1>
          <div className="flex gap-2" data-id="element-1190">
            <button onClick={() => setActiveStrategy('CONSERVATIVE')} className={`px-4 py-2 text-sm font-medium rounded border ${activeStrategy === 'CONSERVATIVE' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-1191">
              Conservative
            </button>
            <button onClick={() => setActiveStrategy('BALANCED')} className={`px-4 py-2 text-sm font-medium rounded border ${activeStrategy === 'BALANCED' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-1192">
              Balanced
            </button>
            <button onClick={() => setActiveStrategy('AGGRESSIVE')} className={`px-4 py-2 text-sm font-medium rounded border ${activeStrategy === 'AGGRESSIVE' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-1193">
              Aggressive
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1194">
        {/* Left Column – Pipeline + Queue */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1195">
          {/* Actionable Pipeline */}
          <div className="bg-white rounded-lg border border-gray-200" data-id="element-1196">
            <div className="p-5 border-b border-gray-200" data-id="element-1197">
              <div className="flex items-center justify-between" data-id="element-1198">
                <h2 className="text-sm font-semibold text-gray-900 uppercase" data-id="element-1199">
                  Actionable pipeline
                </h2>
                <div className="flex gap-2 text-xs" data-id="element-1200">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full" data-id="element-1201">
                    IG
                  </span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full" data-id="element-1202">
                    ≤90D
                  </span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full" data-id="element-1203">
                    Balanced
                  </span>
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-200" data-id="element-1204">
              {pipelineGroups.map(group => <div key={group.id} data-id="element-1205">
                  <button onClick={() => setExpandedPipeline(expandedPipeline === group.id ? null : group.id)} className="w-full p-5 hover:bg-gray-50 transition-colors text-left" data-id="element-1206">
                    <div className="flex items-center justify-between" data-id="element-1207">
                      <div className="flex items-center gap-3" data-id="element-1208">
                        <span className="text-base font-semibold text-gray-900" data-id="element-1209">
                          {group.name}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-900 text-white text-xs font-medium rounded-full" data-id="element-1210">
                          {group.rating}
                        </span>
                      </div>
                      <ChevronRightIcon size={18} className={`text-gray-400 transition-transform ${expandedPipeline === group.id ? 'rotate-90' : ''}`} data-id="element-1211" />
                    </div>
                    <div className="mt-2 flex items-center gap-6 text-sm text-gray-600" data-id="element-1212">
                      <span data-id="element-1213">{group.invoices} invoices</span>
                      <span data-id="element-1214">${group.totalValue}M</span>
                      <span data-id="element-1215">{group.trades} trades</span>
                      <span data-id="element-1216">{group.collection}% collection</span>
                      <span data-id="element-1217">{group.avgDays}D avg</span>
                    </div>
                  </button>
                  {expandedPipeline === group.id && group.deals.length > 0 && <div className="px-5 pb-5 space-y-2" data-id="element-1218">
                      {group.deals.map(deal => <div key={deal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-100" data-id="element-1219">
                          <div data-id="element-1220">
                            <div className="font-medium text-gray-900" data-id="element-1221">
                              {deal.supplier} • ${deal.amount}M
                            </div>
                            <div className="text-sm text-gray-600" data-id="element-1222">
                              Model {deal.model} • {deal.category}
                            </div>
                          </div>
                          <button className="px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded hover:bg-gray-800" onClick={() => handleAddDealToQueue(group, deal)} data-id="element-1223">
                            + Add
                          </button>
                        </div>)}
                      <button className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 font-medium" data-id="element-1224">
                        Add all eligible
                      </button>
                    </div>}
                </div>)}
            </div>
          </div>

          {/* Allocation Queue */}
          <div className="bg-white rounded-lg border border-gray-200" data-id="element-1225">
            <div className="p-5 border-b border-gray-200" data-id="element-1226">
              <div className="flex items-center justify-between" data-id="element-1227">
                <h2 className="text-sm font-semibold text-gray-900 uppercase" data-id="element-1228">
                  Allocation queue
                </h2>
                <div className="flex gap-2" data-id="element-1229">
                  <button className="px-3 py-1.5 bg-white border border-gray-300 text-xs font-medium rounded hover:bg-gray-50" data-id="element-1230">
                    Reset queue
                  </button>
                  <span className="px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded" data-id="element-1231">
                    {allocationQueue.filter(i => i.status !== 'SETTLED').length}{' '}
                    active
                  </span>
                  <span className="px-3 py-1.5 bg-gray-700 text-white text-xs font-medium rounded" data-id="element-1232">
                    {allocationQueue.filter(i => i.status === 'SETTLED').length}{' '}
                    settled
                  </span>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-1233">
              <table className="min-w-full text-xs" data-id="element-1234">
                <thead className="bg-gray-50 text-gray-600 uppercase" data-id="element-1235">
                  <tr data-id="element-1236">
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1237">ID</th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1238">
                      Obligor / Supplier
                    </th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1239">Amount</th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1240">Status</th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1241">SLA</th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1242">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-1243">
                  {allocationQueue.map(item => <tr key={item.id} className="hover:bg-gray-50" data-id="element-1244">
                      <td className="px-4 py-3 font-medium text-gray-900" data-id="element-1245">
                        {item.id}
                      </td>
                      <td className="px-4 py-3" data-id="element-1246">
                        <div className="font-medium text-gray-900" data-id="element-1247">
                          {item.obligor}
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-1248">
                          {item.supplier}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900" data-id="element-1249">
                        ${(item.amount / 1000).toFixed(0)}K
                      </td>
                      <td className="px-4 py-3" data-id="element-1250">
                        <div className="flex items-center gap-2" data-id="element-1251">
                          <div className="w-2 h-2 rounded-full bg-gray-400" data-id="element-1252" />
                          <span className="text-xs text-gray-700" data-id="element-1253">
                            {item.status === 'UCC FILING' ? 'UCC filing' : item.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-1254">
                        {item.status === 'SETTLED' ? <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-[11px] font-medium rounded-full" data-id="element-1255">
                            Settled
                          </span> : <div className="flex items-center gap-2" data-id="element-1256">
                            <span className="text-xs text-gray-900" data-id="element-1257">
                              {item.sla}
                            </span>
                            {item.priority === 'HIGH' && <span className="px-2 py-0.5 bg-gray-900 text-white text-[11px] font-medium rounded-full" data-id="element-1258">
                                High
                              </span>}
                          </div>}
                      </td>
                      <td className="px-4 py-3" data-id="element-1259">
                        {item.status !== 'SETTLED' && <div className="flex items-center gap-1" data-id="element-1260">
                            <button className="p-1 hover:bg-gray-100 rounded" data-id="element-1261">
                              <ZapIcon size={14} className="text-gray-600" data-id="element-1262" />
                            </button>
                            <button className="p-1 hover:bg-gray-100 rounded" data-id="element-1263">
                              <PauseIcon size={14} className="text-gray-600" data-id="element-1264" />
                            </button>
                            <button className="p-1 hover:bg-gray-100 rounded" data-id="element-1265">
                              <XIcon size={14} className="text-gray-600" data-id="element-1266" />
                            </button>
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {/* Pending Allocation – restyled as white card */}
            <div className="border-t border-gray-200 p-5 bg-gray-50" data-id="element-1267">
              <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-1268">
                Pending allocation
              </div>
              <div className="grid grid-cols-3 gap-6 text-sm" data-id="element-1269">
                <div data-id="element-1270">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-1271">Next sweep</div>
                  <div className="font-medium text-gray-900" data-id="element-1272">
                    14:00 EST (in 23 min)
                  </div>
                </div>
                <div data-id="element-1273">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-1274">
                    Available cash
                  </div>
                  <div className="text-xl font-semibold text-gray-900" data-id="element-1275">
                    $1.5M
                  </div>
                </div>
                <div data-id="element-1276">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-1277">
                    Eligible deals
                  </div>
                  <div className="text-xl font-semibold text-gray-900" data-id="element-1278">7</div>
                  <div className="text-xs text-gray-500" data-id="element-1279">$8.4M</div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-white border border-gray-200 rounded-md" data-id="element-1280">
                <div className="text-xs text-gray-500 mb-1" data-id="element-1281">
                  Projected allocation
                </div>
                <div className="text-sm font-semibold text-gray-900" data-id="element-1282">
                  2 deals @ $1.4M
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column – Portfolio monitor & risk */}
        <div className="space-y-6" data-id="element-1283">
          {/* Active Portfolio Monitor */}
          <div className="bg-white rounded-lg border border-gray-200 p-5" data-id="element-1284">
            <h2 className="text-xs font-semibold text-gray-700 uppercase mb-4" data-id="element-1285">
              Active portfolio monitor
            </h2>
            <div className="space-y-4" data-id="element-1286">
              <div data-id="element-1287">
                <div className="text-xs text-gray-500 uppercase mb-1" data-id="element-1288">
                  Deployed capital
                </div>
                <div className="flex items-baseline gap-2" data-id="element-1289">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-1290">$98.5M</div>
                  <TrendingUpIcon size={14} className="text-green-600" data-id="element-1291" />
                </div>
                <div className="text-xs text-gray-600 mt-1" data-id="element-1292">98.5% utilized</div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1293">
                <div data-id="element-1294">
                  <div className="text-xs text-gray-500 uppercase mb-1" data-id="element-1295">
                    Weighted avg days
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-1296">
                    <div className="text-lg font-bold text-gray-900" data-id="element-1297">42.3d</div>
                    <TrendingUpIcon size={12} className="text-green-600" data-id="element-1298" />
                  </div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-1299">↓ 2.1</div>
                </div>
                <div data-id="element-1300">
                  <div className="text-xs text-gray-500 uppercase mb-1" data-id="element-1301">
                    Collection rate
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-1302">
                    <div className="text-lg font-bold text-gray-900" data-id="element-1303">98.7%</div>
                    <TrendingUpIcon size={12} className="text-green-600" data-id="element-1304" />
                  </div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-1305">On track</div>
                </div>
              </div>
              <div data-id="element-1306">
                <div className="text-xs text-gray-500 uppercase mb-1" data-id="element-1307">
                  Projected IRR
                </div>
                <div className="flex items-baseline gap-2" data-id="element-1308">
                  <div className="text-lg font-bold text-gray-900" data-id="element-1309">14.7%</div>
                  <TrendingUpIcon size={12} className="text-green-600" data-id="element-1310" />
                </div>
                <div className="text-xs text-gray-600 mt-1" data-id="element-1311">Target 11%</div>
              </div>
              {/* Concentration Risk */}
              <div className="pt-4 border-t border-gray-200" data-id="element-1312">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-1313">
                  Concentration risk
                </div>
                <div className="space-y-2 text-xs" data-id="element-1314">
                  <ConcentrationRow label="Walmart" value="18.2%" tone="warn" data-id="element-1315" />
                  <ConcentrationRow label="Amazon" value="12.4%" tone="pass" data-id="element-1316" />
                  <ConcentrationRow label="Target" value="10.2%" tone="pass" data-id="element-1317" />
                  <ConcentrationRow label="Top 5" value="52.8%" tone="watch" data-id="element-1318" />
                </div>
              </div>
              {/* Aging Buckets */}
              <div className="pt-4 border-t border-gray-200" data-id="element-1319">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-1320">
                  Aging buckets
                </div>
                <div className="space-y-2" data-id="element-1321">
                  {agingBuckets.map(bucket => <div key={bucket.range} data-id="element-1322">
                      <div className="flex items-center justify-between text-xs mb-1" data-id="element-1323">
                        <span className="text-gray-600" data-id="element-1324">{bucket.range}</span>
                        <span className="font-medium text-gray-900" data-id="element-1325">
                          {bucket.pct}% • ${bucket.value}M
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-1326">
                        <div className="h-1.5 rounded-full bg-gray-700" style={{
                      width: `${bucket.pct}%`
                    }} data-id="element-1327" />
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* Active Risk Management */}
          <div className="bg-white rounded-lg border border-gray-200" data-id="element-1328">
            <div className="p-5 border-b border-gray-200" data-id="element-1329">
              <div className="flex items-center justify-between" data-id="element-1330">
                <h2 className="text-xs font-semibold text-gray-700 uppercase" data-id="element-1331">
                  Active risk management
                </h2>
                <span className="px-2 py-0.5 bg-gray-900 text-white text-[11px] font-medium rounded-full" data-id="element-1332">
                  Compliant
                </span>
              </div>
            </div>
            <div className="p-5 space-y-4 text-sm" data-id="element-1333">
              <div data-id="element-1334">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-2" data-id="element-1335">
                  Covenant compliance
                </div>
                <div className="space-y-2 text-xs" data-id="element-1336">
                  <LimitStrip label="Obligor concentration < 20%" value="18.2%" tone="warn" data-id="element-1337" />
                  <LimitStrip label="Top 5 concentration < 60%" value="52.8%" tone="watch" data-id="element-1338" />
                  <LimitStrip label="Sector limit < 35%" value="31.0%" tone="warn" data-id="element-1339" />
                  <LimitStrip label="Model +% labeling" value="Projected, not realized" tone="model" data-id="element-1340" />
                </div>
              </div>
              <div data-id="element-1341">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-2" data-id="element-1342">
                  Portfolio limits
                </div>
                <div className="space-y-3 text-xs" data-id="element-1343">
                  <div className="flex items-start gap-2" data-id="element-1344">
                    <AlertTriangleIcon size={14} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1345" />
                    <div data-id="element-1346">
                      <div className="text-gray-900" data-id="element-1347">
                        Walmart exposure approaching 20% limit
                      </div>
                      <button className="text-gray-700 mt-1 hover:text-gray-900" data-id="element-1348">
                        Request override →
                      </button>
                    </div>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1349">
                    <InfoIcon size={14} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1350" />
                    <div className="text-gray-900" data-id="element-1351">
                      Consider diversification trades
                    </div>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1352">
                    <InfoIcon size={14} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1353" />
                    <div className="text-gray-900" data-id="element-1354">
                      Next review: Thursday 10AM
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-1355">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-2" data-id="element-1356">
                  Real-time alerts
                </div>
                <div className="space-y-2 text-xs" data-id="element-1357">
                  <div className="flex items-start gap-2" data-id="element-1358">
                    <span className="text-gray-500 w-10" data-id="element-1359">09:42</span>
                    <AlertTriangleIcon size={12} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1360" />
                    <span className="text-gray-900" data-id="element-1361">
                      Walmart downgraded to A2
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1362">
                    <span className="text-gray-500 w-10" data-id="element-1363">09:38</span>
                    <CheckCircleIcon size={12} className="text-gray-700 flex-shrink-0 mt-0.5" data-id="element-1364" />
                    <span className="text-gray-900" data-id="element-1365">
                      Premium Foods: $234K collection
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1366">
                    <span className="text-gray-500 w-10" data-id="element-1367">09:31</span>
                    <InfoIcon size={12} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1368" />
                    <span className="text-gray-900" data-id="element-1369">
                      AI: Target $4.2M @ 13.5% detected
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
/* helper components */
function ConcentrationRow({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone: 'pass' | 'warn' | 'watch';
}) {
  const chip = tone === 'pass' ? 'bg-gray-100 text-gray-800' : tone === 'warn' ? 'bg-gray-100 text-gray-800' : 'bg-gray-900 text-white';
  return <div className="flex items-center justify-between" data-id="element-1370">
      <span className="text-gray-900" data-id="element-1371">
        {label}: {value}
      </span>
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${chip}`} data-id="element-1372">
        {tone === 'pass' ? 'Pass' : tone === 'warn' ? 'Warn' : 'Watch'}
      </span>
    </div>;
}
function LimitStrip({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone: 'pass' | 'warn' | 'watch' | 'model';
}) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium';
  const chip = tone === 'model' ? 'bg-gray-900 text-white' : tone === 'pass' ? 'bg-gray-100 text-gray-800' : tone === 'warn' ? 'bg-gray-100 text-gray-800' : 'bg-gray-900 text-white';
  return <div className="flex items-center justify-between" data-id="element-1373">
      <span className="text-gray-700" data-id="element-1374">{label}</span>
      <div className="flex items-center gap-2" data-id="element-1375">
        <span className="text-gray-900 text-xs" data-id="element-1376">{value}</span>
        <span className={`${base} ${chip}`} data-id="element-1377">
          {tone === 'model' ? 'Model' : tone === 'pass' ? 'Pass' : tone === 'warn' ? 'Warn' : 'Watch'}
        </span>
      </div>
    </div>;
}